import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { PRESS } from "@/data/content";

const ACCENT = {
  coral: "bg-coral text-white",
  yellow: "bg-yellow text-obsidian",
  blue: "bg-blue text-obsidian",
  obsidian: "bg-obsidian text-white",
};

export const Press = () => {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? PRESS : PRESS.slice(0, 3);
  const hasMore = PRESS.length > 3;
  return (
    <section
      id="press"
      data-testid="press-section"
      className="bg-white text-obsidian py-20 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-12 md:mb-16">
          <span className="font-outfit text-xs uppercase tracking-widest text-coral">
            In The Press
          </span>
          <h2 className="font-anton uppercase text-5xl md:text-7xl leading-[0.9] mt-3">
            Featured <span className="text-blue">In</span>
          </h2>
          <p className="font-outfit font-medium text-obsidian/70 mt-4 max-w-2xl">
            Stories about the work, straight from the outlets that covered it 📰
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {displayed.map((item, i) => (
            <motion.a
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              whileHover={{ y: -6 }}
              data-testid={`press-card-${i}`}
              className={`group relative bg-white rounded-3xl overflow-hidden border-2 border-obsidian/10 shadow-xl hover:shadow-2xl transition-shadow duration-300 ${
                !showAll && i === 2 ? "md:col-span-2" : ""
              }`}
            >
              {item.image && (
                <div className="aspect-[16/9] overflow-hidden bg-obsidian/5">
                  <img
                    src={item.image}
                    alt={item.headline}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}

              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <img
                    src={item.logo}
                    alt={item.outlet}
                    className={item.logoClass || "h-8 md:h-10 w-auto object-contain"}
                    data-testid={`press-logo-${i}`}
                  />
                  <span
                    className={`inline-flex items-center gap-1 font-outfit font-bold text-[10px] md:text-xs uppercase tracking-wide px-3 py-1.5 rounded-full ${
                      ACCENT[item.accent] || ACCENT.coral
                    }`}
                  >
                    {item.date}
                  </span>
                </div>

                <h3 className="font-anton uppercase text-2xl md:text-3xl leading-tight mb-3">
                  {item.headline}
                </h3>
                {item.excerpt && (
                  <p className="font-outfit text-sm md:text-base text-obsidian/70 leading-snug">
                    {item.excerpt}
                  </p>
                )}

                <div className="mt-6 inline-flex items-center gap-2 font-outfit font-extrabold text-sm uppercase tracking-wide text-blue group-hover:text-coral transition-colors">
                  Read the story
                  <ArrowUpRight
                    size={18}
                    className="group-hover:rotate-45 transition-transform duration-300"
                  />
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {hasMore && (
          <div className="mt-10 md:mt-14 flex justify-center">
            <motion.button
              data-testid="press-toggle"
              onClick={() => setShowAll((v) => !v)}
              whileHover={{ scale: 1.05, rotate: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 14 }}
              className="inline-flex items-center gap-2 font-outfit font-extrabold text-sm md:text-base uppercase tracking-wide bg-obsidian text-white px-7 py-4 rounded-full hover:bg-coral transition-colors duration-200"
            >
              {showAll ? "Show Less" : `See All Press! (${PRESS.length})`}
              <ChevronDown
                size={18}
                className={`transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}
              />
            </motion.button>
          </div>
        )}
      </div>
    </section>
  );
};
